import type { ClassDefinition } from '../data/types';
import type { InputDevice } from '../input/intents';
import type { PlayerState } from '../sim/state';
import { t } from '../shared/i18n';
import { el, keycap, setFlag, setText, setVar } from './dom';
import { formatNumber, ratio } from './format';
import { playerLabel, skillCharge } from './hud-model';
import type { Prompts } from './prompts';
import { createWho, fillWho, whoColor } from './who';

export interface SkillView {
  root: HTMLElement;
  glyph: HTMLElement;
  name: HTMLElement;
  key: HTMLElement;
  status: HTMLElement;
}

export function createSkillView(): SkillView {
  const root = el('div', 'ui-skill');
  const ring = el('div', 'ui-skill__ring');
  const glyph = el('span', 'ui-skill__glyph');
  ring.append(glyph);
  const name = el('span', 'ui-skill__name');
  const key = el('span', 'ui-skill__key');
  const status = el('span', 'ui-skill__status');
  const text = el('div', 'ui-skill__text');
  text.append(name, status);
  root.append(ring, key, text);
  return { root, glyph, name, key, status };
}

export interface Bracelet {
  readonly element: HTMLElement;
  // The device is that of a player of this screen, whose skill key the bracelet shows; null for the others.
  update(player: PlayerState, definition: ClassDefinition | null, device: InputDevice | null): void;
}

const CRITICAL_RATIO = 0.25;

export function createBracelet(prompts: Prompts): Bracelet {
  const element = el('section', 'ui-panel ui-bracelet');
  const who = createWho();
  const level = el('span', 'ui-bracelet__level');
  const status = el('span', 'ui-bracelet__status');
  const fireflies = el('span', 'ui-bracelet__fireflies');
  fireflies.setAttribute('aria-hidden', 'true');
  fireflies.append(...[0, 1, 2].map(() => el('span', 'ui-bracelet__firefly')));
  const head = el('div', 'ui-bracelet__head');
  head.append(fireflies, who, level, status);
  const life = el('div', 'ui-bar ui-bracelet__life');
  life.setAttribute('role', 'meter');
  life.setAttribute('aria-label', t('Vie'));
  life.setAttribute('aria-valuemin', '0');
  life.append(el('span', 'ui-bar__fill'));
  const body = el('div', 'ui-bracelet__body');
  body.append(head, life);
  const skill = el('span', 'ui-bracelet__skill');
  const key = el('span', 'ui-bracelet__key');
  element.append(body, skill, key);

  let builtDevice: InputDevice | null = null;
  let builtKeys = -1;

  return {
    element,
    update(player, definition, device) {
      fillWho(who, player);
      setVar(element, '--who', whoColor(player.classId));
      const fraction = ratio(player.hp, player.maxHp);
      setVar(life, '--fill', String(fraction));
      life.setAttribute('aria-valuemax', String(player.maxHp));
      life.setAttribute('aria-valuenow', String(Math.round(player.hp)));
      life.setAttribute(
        'aria-valuetext',
        t('{hp} sur {maxHp}', { hp: formatNumber(player.hp), maxHp: formatNumber(player.maxHp) }),
      );
      setFlag(element, 'critical', !player.downed && fraction <= CRITICAL_RATIO);
      setFlag(element, 'downed', player.downed);
      setText(level, t('Niv. {level}', { level: player.level }));
      setText(status, player.downed ? t('À terre') : '');
      element.setAttribute('aria-label', playerLabel(player));
      if (device !== builtDevice || builtKeys !== prompts.version()) {
        builtDevice = device;
        builtKeys = prompts.version();
        const shown = device === null ? null : prompts.for(device);
        key.replaceChildren(...(shown === null ? [] : [keycap(shown.skill, shown.style)]));
        key.hidden = shown === null;
      }
      const ready = definition !== null && player.skillCooldown <= 0;
      setVar(
        skill,
        '--charge',
        definition === null ? '0' : String(skillCharge(player, definition.skill)),
      );
      setFlag(skill, 'ready', ready);
      if (definition === null) {
        skill.title = '';
      } else {
        const params = { skill: t('Spéciale') };
        skill.title = ready ? t('{skill} : prête', params) : t('{skill} : en recharge', params);
      }
    },
  };
}
